import React, { useState, useEffect, useCallback } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Common/Loader';
import { API_URL } from '../../config';

interface PromoCodeData {
    id: number;
    code: string;
    description: string | null;
    role_id: number;
    duration_days: number;
    max_uses: number;
    current_uses: number;
    is_active: boolean;
    expires_at: string | null;
    created_at: string;
    is_referral_code?: boolean;
    referrer_id?: number | null;
    commission_pct?: number;
    discount_pct?: number;
}

interface RoleOption {
    id: number;
    name: string;
}

interface User {
    id: number;
    email: string;
    full_name: string | null;
}

const AdminPromoCodesScreen: React.FC = () => {
    const { token } = useAuth();
    const [promoCodes, setPromoCodes] = useState<PromoCodeData[]>([]);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Form state
    const [code, setCode] = useState('');
    const [description, setDescription] = useState('');
    const [roleId, setRoleId] = useState<number>(0);
    const [durationDays, setDurationDays] = useState(30);
    const [maxUses, setMaxUses] = useState(100);
    const [expiresAt, setExpiresAt] = useState('');

    // Referral state
    const [isReferralCode, setIsReferralCode] = useState(false);
    const [referrerId, setReferrerId] = useState<number | null>(null);
    const [commissionPct, setCommissionPct] = useState(10);
    const [discountPct, setDiscountPct] = useState(10);
    const [userSearch, setUserSearch] = useState('');

    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const fetchPromoCodes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/promo-codes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setPromoCodes(await response.json());
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/roles`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setRoles(data);
                if (data.length > 0 && roleId === 0) {
                    // Default to paid_user if exists
                    const targetRole = data.find((r: RoleOption) => r.name === 'paid_user') || data.find((r: RoleOption) => r.name === 'paid_user_pro');
                    setRoleId(targetRole ? targetRole.id : data[0].id);
                }
            }
        } catch (error) {
            console.error(error);
        }
    }, [token, roleId]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (error) {
            console.error(error);
        }
    }, [token]);

    useEffect(() => {
        fetchPromoCodes();
        fetchRoles();
        fetchUsers();
    }, [token, fetchPromoCodes, fetchRoles, fetchUsers]);

    const handleOpenModal = (promo?: PromoCodeData) => {
        setMessage(null);
        if (promo) {
            setEditingId(promo.id);
            setCode(promo.code);
            setDescription(promo.description || '');
            setRoleId(promo.role_id);
            setDurationDays(promo.duration_days);
            setMaxUses(promo.max_uses);
            setExpiresAt(promo.expires_at ? promo.expires_at.split('T')[0] : '');
            setIsReferralCode(promo.is_referral_code || false);
            setReferrerId(promo.referrer_id || null);
            setCommissionPct(promo.commission_pct || 10);
            setDiscountPct(promo.discount_pct || 10);
        } else {
            setEditingId(null);
            setCode('');
            setDescription('');
            const targetRole = roles.find(r => r.name === 'paid_user') || roles.find(r => r.name === 'paid_user_pro');
            setRoleId(targetRole ? targetRole.id : (roles[0]?.id || 0));
            setDurationDays(30);
            setMaxUses(100);
            setExpiresAt('');
            setIsReferralCode(false);
            setReferrerId(null);
            setCommissionPct(10);
            setDiscountPct(10);
        }
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setProcessing(true);
        setMessage(null);

        if (isReferralCode && !referrerId) {
            setMessage({ type: 'error', text: 'Veuillez sélectionner un parrain pour le mode parrainage.' });
            setProcessing(false);
            return;
        }

        const payload: any = {
            code: code.toUpperCase(),
            description: description || null,
            role_id: roleId,
            duration_days: durationDays,
            max_uses: maxUses,
            expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
            is_referral_code: isReferralCode,
            referrer_id: isReferralCode ? referrerId : null,
            commission_pct: isReferralCode ? commissionPct : 0,
            discount_pct: discountPct,
        };

        try {
            const url = editingId
                ? `${API_URL}/v1/admin/promo-codes/${editingId}`
                : `${API_URL}/v1/admin/promo-codes`;

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setMessage({ type: 'success', text: editingId ? 'Code promo mis à jour !' : 'Code promo créé avec succès !' });
                setShowModal(false);
                fetchPromoCodes();
            } else {
                const err = await response.json();
                setMessage({ type: 'error', text: err.detail || 'Erreur' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Erreur de connexion' });
        } finally {
            setProcessing(false);
        }
    };

    const handleToggle = async (promoId: number) => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/promo-codes/${promoId}/toggle`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                fetchPromoCodes();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (promoId: number) => {
        if (!window.confirm('Supprimer ce code promo ?')) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/promo-codes/${promoId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setMessage({ type: 'success', text: 'Code promo supprimé.' });
                fetchPromoCodes();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getRoleName = (id: number) => {
        const role = roles.find(r => r.id === id);
        return role ? role.name : `ID ${id}`;
    };

    return (
        <SiteLayout>
            <Header icon='confirmation_number' title='Gestion des Codes Promo' />

            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <button
                        onClick={() => handleOpenModal()}
                        style={{
                            padding: '10px 20px',
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        + Nouveau Code Promo
                    </button>
                </div>

                {message && (
                    <div style={{
                        padding: '15px',
                        marginBottom: '20px',
                        borderRadius: '5px',
                        background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                        color: message.type === 'success' ? '#166534' : '#991b1b',
                    }}>
                        {message.text}
                    </div>
                )}

                <Box>
                    <div style={{ padding: '20px' }}>
                        <h3 style={{ marginBottom: '20px' }}>Codes Promo ({promoCodes.length})</h3>

                        {loading ? (
                            <Loader />
                        ) : promoCodes.length === 0 ? (
                            <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>Aucun code promo trouvé.</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border-color)' }}>
                                        <th style={{ padding: '12px' }}>Code</th>
                                        <th style={{ padding: '12px' }}>Rôle cible</th>
                                        <th style={{ padding: '12px' }}>Durée</th>
                                        <th style={{ padding: '12px' }}>Utilisation</th>
                                        <th style={{ padding: '12px' }}>Statut</th>
                                        <th style={{ padding: '12px' }}>Expire le</th>
                                        <th style={{ padding: '12px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {promoCodes.map((promo) => {
                                        const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
                                        const isExhausted = promo.current_uses >= promo.max_uses;

                                        return (
                                            <tr key={promo.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '14px' }}>{promo.code}</div>
                                                        {promo.discount_pct && promo.discount_pct > 0 ? (
                                                            <span
                                                                style={{ background: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', border: '1px solid #10b981' }}
                                                            >
                                                                -{promo.discount_pct}%
                                                            </span>
                                                        ) : null}
                                                        {promo.is_referral_code && (
                                                            promo.commission_pct && promo.commission_pct > 0 ? (
                                                                <span
                                                                    title={`Partenaire Coach Agréé : ${promo.commission_pct}% commission`}
                                                                    style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', border: '1px solid #f59e0b' }}
                                                                >
                                                                    🤝 COACH / PARTENAIRE
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    title="Parrainage Utilisateur Standard (30 jours offerts)"
                                                                    style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', border: '1px solid #0ea5e9' }}
                                                                >
                                                                    👥 PARRAINAGE USER
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                    {promo.description && (
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{promo.description}</div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{
                                                        background: '#eff6ff',
                                                        color: '#1d4ed8',
                                                        padding: '2px 8px',
                                                        borderRadius: '10px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {getRoleName(promo.role_id)}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px' }}>{promo.duration_days} jours</td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{
                                                        fontWeight: 'bold',
                                                        color: isExhausted ? '#991b1b' : 'var(--text-main)'
                                                    }}>
                                                        {promo.current_uses}/{promo.max_uses}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    {!promo.is_active ? (
                                                        <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>INACTIF</span>
                                                    ) : isExpired ? (
                                                        <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>EXPIRÉ</span>
                                                    ) : isExhausted ? (
                                                        <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>ÉPUISÉ</span>
                                                    ) : (
                                                        <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>ACTIF</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '13px' }}>
                                                    {promo.expires_at
                                                        ? new Date(promo.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                                                        : <span style={{ color: 'var(--text-muted)' }}>Illimité</span>
                                                    }
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <button
                                                            onClick={() => handleOpenModal(promo)}
                                                            style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '12px' }}
                                                            title="Éditer"
                                                        >✎</button>
                                                        <button
                                                            onClick={() => handleToggle(promo.id)}
                                                            style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', color: promo.is_active ? '#f59e0b' : '#10b981', fontSize: '12px' }}
                                                            title={promo.is_active ? 'Désactiver' : 'Activer'}
                                                        >{promo.is_active ? '⏸' : '▶'}</button>
                                                        <button
                                                            onClick={() => handleDelete(promo.id)}
                                                            style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', color: '#ef4444', fontSize: '12px' }}
                                                            title="Supprimer"
                                                        >🗑</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </Box>
            </div>

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'var(--bg-box)',
                        padding: '30px',
                        borderRadius: '10px',
                        width: '500px',
                        maxWidth: '90%',
                    }}>
                        <h2 style={{ marginBottom: '20px' }}>{editingId ? 'Modifier le code promo' : 'Nouveau code promo'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Code</label>
                                <input
                                    type="text"
                                    required
                                    value={code}
                                    onChange={e => setCode(e.target.value.toUpperCase())}
                                    placeholder="EX: KANEAPRO90"
                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)', fontFamily: 'monospace', letterSpacing: '1px' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description (optionnel)</label>
                                <input
                                    type="text"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="Code de lancement..."
                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Rôle cible</label>
                                <select
                                    value={roleId}
                                    onChange={e => setRoleId(Number(e.target.value))}
                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                                >
                                    {roles.filter(r => r.name !== 'global_admin').map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Durée (jours)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={durationDays}
                                        onChange={e => setDurationDays(Number(e.target.value))}
                                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Utilisations max</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={maxUses}
                                        onChange={e => setMaxUses(Number(e.target.value))}
                                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Date d'expiration (optionnel)</label>
                                <input
                                    type="date"
                                    value={expiresAt}
                                    onChange={e => setExpiresAt(e.target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                                />
                                <small style={{ color: 'var(--text-muted)' }}>Laisser vide = pas d'expiration</small>
                            </div>

                            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', padding: '15px', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Réduction (%)</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={discountPct}
                                            onChange={e => setDiscountPct(Number(e.target.value))}
                                            style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)' }}
                                        />
                                        <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>%</span>
                                    </div>
                                    <small style={{ color: 'var(--text-muted)' }}>Remise sur le prix d'achat</small>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px', padding: '15px', background: 'rgba(37, 99, 235, 0.05)', borderRadius: '8px', border: '1px solid rgba(37, 99, 235, 0.1)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isReferralCode ? '15px' : '0' }}>
                                    <div>
                                        <label style={{ fontWeight: 'bold', display: 'block' }}>🤝 Mode Parrainage</label>
                                        <small style={{ color: 'var(--text-muted)' }}>Associer ce code à un partenaire</small>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={isReferralCode}
                                        onChange={e => setIsReferralCode(e.target.checked)}
                                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                    />
                                </div>

                                {isReferralCode && (
                                    <div style={{ marginTop: '15px', borderTop: '1px solid rgba(37, 99, 235, 0.1)', paddingTop: '15px' }}>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Parrain (Referrer)</label>
                                            <input
                                                type="text"
                                                placeholder="Rechercher un utilisateur..."
                                                value={userSearch}
                                                onChange={e => setUserSearch(e.target.value)}
                                                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)', marginBottom: '5px', fontSize: '13px' }}
                                            />
                                            <select
                                                required={isReferralCode}
                                                value={referrerId || ''}
                                                onChange={e => setReferrerId(Number(e.target.value))}
                                                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)', fontSize: '13px' }}
                                            >
                                                <option value="">-- Sélectionner le parrain --</option>
                                                {users
                                                    .filter(u => !userSearch || u.email.toLowerCase().includes(userSearch.toLowerCase()) || (u.full_name && u.full_name.toLowerCase().includes(userSearch.toLowerCase())))
                                                    .slice(0, 50)
                                                    .map(user => (
                                                        <option key={user.id} value={user.id}>{user.full_name || 'Sans nom'} ({user.email})</option>
                                                    ))
                                                }
                                            </select>
                                        </div>

                                        <div style={{ display: 'flex', gap: '15px' }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Commission (%)</label>
                                                <input
                                                    type="number"
                                                    required={isReferralCode}
                                                    min={0}
                                                    max={100}
                                                    value={commissionPct}
                                                    onChange={e => setCommissionPct(Number(e.target.value))}
                                                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-main)', fontSize: '13px' }}
                                                />
                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: '1.4' }}>
                                                    <strong>💡 Règle de Parrainage :</strong><br />
                                                    • Si <strong>0%</strong> : Parrainage Utilisateur standard (le parrain gagne 30 jours Pro offerts).<br />
                                                    • Si <strong>&gt; 0%</strong> : Parrainage Coach Agréé (rétro-commission financière en XOF).
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{ padding: '10px 20px', background: 'var(--bg-input)', border: 'none', borderRadius: '5px', cursor: 'pointer', color: 'var(--text-main)' }}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={processing}
                                    style={{
                                        padding: '10px 20px',
                                        background: processing ? '#93c5fd' : '#2563eb',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: processing ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {processing ? 'En cours...' : editingId ? 'Mettre à jour' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminPromoCodesScreen;
